import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Perfil de Entidad (single-tenant: ESTA es tu empresa)
   ============================================================ */

function EntityProfile() {
  const { state, dispatch } = useStore();
  const e = state.entity;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(e);

  useEffect(() => { setForm(e); }, [editing]);

  const save = () => {
    dispatch({ type: "UPDATE_ENTITY", patch: form });
    setEditing(false);
  };

  const initials = e.name.split(" ").map(w => w[0]).filter(c => /[A-Za-zÁÉÍÓÚÑ]/.test(c)).slice(0, 2).join("");
  const founded = e.founded || 2000;
  const yearsActive = new Date().getFullYear() - founded;

  return (
    <div className="stack" style={{ gap: 22 }}>
      {/* ===== HERO ===== */}
      <div className="entity-hero">
        <div className="sticker-1"></div>
        <div className="sticker-2"></div>

        <div style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 22, marginBottom: 22 }}>
          <div className="entity-monogram">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Badge style={{ background: "rgba(255,255,255,0.6)", color: "var(--brand-700)", fontWeight: 700 }}>
                <span className="dot" style={{ background: "var(--good-500)" }}></span> Espacio activo
              </Badge>
              <Badge style={{ background: "rgba(15,18,26,0.06)", color: "var(--ink-700)" }}>
                Despliegue dedicado
              </Badge>
            </div>
            {editing ? (
              <input className="input"
                value={form.name}
                onChange={ev => setForm({...form, name: ev.target.value})}
                style={{ fontSize: 38, fontWeight: 700, letterSpacing: "-0.03em", padding: "4px 10px", background: "rgba(255,255,255,0.7)" }}
              />
            ) : (
              <h1 style={{ margin: 0, fontSize: 40, fontWeight: 700, letterSpacing: "-0.035em", lineHeight: 1.05 }}>
                {e.name}
              </h1>
            )}
            <div style={{ marginTop: 8, fontSize: 14.5, color: "var(--ink-700)" }}>
              {e.sector} · CIF <span className="mono">{e.cif}</span> · {e.address.split(",").pop()?.trim() || ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {editing ? (
              <>
                <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={save}>
                  <Icon name="check" size={14} /> Guardar
                </button>
              </>
            ) : (
              <button className="btn btn-secondary" onClick={() => setEditing(true)}>
                <Icon name="edit" size={14} /> Editar perfil
              </button>
            )}
          </div>
        </div>

        {/* Stat strip */}
        <div style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 0,
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(8px)",
          borderRadius: 18,
          padding: "16px 4px",
          border: "1px solid rgba(255,255,255,0.6)",
        }}>
          <HeroStat label="Empleados" value={e.size} note="en plantilla" />
          <HeroStat label="Departamentos" value={state.departments.length} note="áreas activas" divider />
          <HeroStat label="Puestos" value={state.positions.length} note="catalogados" divider />
          <HeroStat label="Antigüedad" value={yearsActive} note={`desde ${founded}`} divider suffix="años" />
        </div>
      </div>

      {/* ===== Main grid ===== */}
      <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr", gap: 18 }}>
        {/* ABOUT */}
        <div className="stack" style={{ gap: 18 }}>
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-eyebrow">Sobre la empresa</div>
                <div className="card-title" style={{ marginTop: 4, fontSize: 16 }}>Descripción</div>
              </div>
            </div>
            {editing ? (
              <textarea className="textarea" rows={5}
                value={form.description}
                onChange={ev => setForm({...form, description: ev.target.value})}
              />
            ) : (
              <p style={{ margin: 0, fontSize: 14.5, color: "var(--ink-700)", lineHeight: 1.65, textWrap: "pretty" }}>
                {e.description}
              </p>
            )}
          </div>

          {/* Datos fiscales */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-eyebrow">Identidad legal</div>
                <div className="card-title" style={{ marginTop: 4, fontSize: 16 }}>Datos fiscales</div>
              </div>
            </div>
            <div className="grid grid-2" style={{ gap: 4 }}>
              <FieldRow icon="building" label="Razón social" editing={editing}
                value={form.name} onChange={v => setForm({...form, name: v})} />
              <FieldRow icon="lock" label="CIF" editing={editing}
                value={form.cif} onChange={v => setForm({...form, cif: v})} mono />
              <FieldRow icon="factory" label="Sector" editing={editing}
                value={form.sector} onChange={v => setForm({...form, sector: v})} />
              <FieldRow icon="calendar" label="Año de fundación" editing={editing}
                value={String(form.founded)} onChange={v => setForm({...form, founded: parseInt(v) || 0})} />
            </div>
          </div>

          {/* Contacto */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-eyebrow">Cómo nos encuentran</div>
                <div className="card-title" style={{ marginTop: 4, fontSize: 16 }}>Contacto y ubicación</div>
              </div>
            </div>
            <div className="stack" style={{ gap: 0 }}>
              <FieldRow icon="map" label="Dirección" editing={editing}
                value={form.address} onChange={v => setForm({...form, address: v})} fullwidth />
              <div className="grid grid-2" style={{ gap: 4 }}>
                <FieldRow icon="phone" label="Teléfono" editing={editing}
                  value={form.phone} onChange={v => setForm({...form, phone: v})} mono />
                <FieldRow icon="mail" label="Email" editing={editing}
                  value={form.email} onChange={v => setForm({...form, email: v})} />
              </div>
              <FieldRow icon="globe" label="Web" editing={editing}
                value={form.web} onChange={v => setForm({...form, web: v})} fullwidth />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="stack" style={{ gap: 18 }}>
          {/* Single-tenant info */}
          <div className="card" style={{
            background: "var(--ink-900)",
            color: "white",
            border: "none",
            position: "relative",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", top: -50, right: -50,
              width: 180, height: 180, borderRadius: "50%",
              background: "var(--brand-500)", opacity: 0.18,
            }} />
            <div style={{
              position: "absolute", bottom: -30, left: -30,
              width: 100, height: 100, borderRadius: "50%",
              background: "white", opacity: 0.05,
            }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>
                <Icon name="lock" size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                Despliegue privado
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.3 }}>
                Esta instancia es exclusiva para {e.name.split(" ").slice(0, 2).join(" ")}
              </div>
              <div style={{ fontSize: 13, opacity: 0.7, marginTop: 8, lineHeight: 1.55 }}>
                Talention funciona como single-tenant: tus datos viven en un entorno aislado, sin mezclarse con otras organizaciones.
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 999, background: "rgba(255,255,255,0.12)", fontWeight: 590 }}>
                  RGPD compliant
                </span>
                <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 999, background: "rgba(255,255,255,0.12)", fontWeight: 590 }}>
                  EU hosted
                </span>
                <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 999, background: "rgba(255,255,255,0.12)", fontWeight: 590 }}>
                  Backup diario
                </span>
              </div>
            </div>
          </div>

          {/* Convenio */}
          <div className="card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: "var(--info-100)", color: "#1d4ed8",
                display: "grid", placeItems: "center", flexShrink: 0,
              }}>
                <Icon name="file-text" size={14} />
              </div>
              <div className="card-eyebrow" style={{ marginBottom: 0 }}>Convenio colectivo</div>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.022em", lineHeight: 1.3, color: "var(--ink-900)", textWrap: "pretty" }}>
              {e.convenio}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
              <Badge tone="info" dot>Vigente</Badge>
              <Badge>Sectorial</Badge>
            </div>
          </div>

          {/* Plan info */}
          <div className="card" style={{ position: "relative", overflow: "hidden" }}>
            <div style={{
              position: "absolute", top: -30, right: -30,
              width: 100, height: 100, borderRadius: "50%",
              background: "var(--brand-500)", opacity: 0.08,
            }} />
            <div style={{ position: "relative" }}>
              <div className="card-eyebrow">Suscripción</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.025em" }}>Enterprise</span>
                <span className="muted tiny">· anual</span>
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>Renueva el 15 de marzo de 2027</div>
              <div className="bar" style={{ marginTop: 14 }}>
                <div className="bar-fill" style={{ width: "62%" }} />
              </div>
              <div className="muted tiny" style={{ marginTop: 6 }}>62% del periodo consumido</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, note, suffix, divider }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "4px 16px", textAlign: "center",
      borderLeft: divider ? "1px solid rgba(15,18,26,0.08)" : "none",
    }}>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.04 + "em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
        {value}{suffix && <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 4, color: "var(--ink-500)" }}>{suffix}</span>}
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>{note}</div>
    </div>
  );
}

function FieldRow({ icon, label, value, editing, onChange, mono, fullwidth }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "12px 4px",
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 9, background: "var(--bg-soft)",
        display: "grid", placeItems: "center", color: "var(--ink-500)", flexShrink: 0,
      }}><Icon name={icon} size={14} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="tiny muted">{label}</div>
        {editing ? (
          <input className="input" style={{ marginTop: 4, padding: "5px 10px", fontSize: 13 }}
                 value={value} onChange={e => onChange(e.target.value)} />
        ) : (
          <div className={"semi " + (mono ? "mono" : "")} style={{ fontSize: 14, marginTop: 1, letterSpacing: "-0.005em" }}>
            {value}
          </div>
        )}
      </div>
    </div>
  );
}

export { EntityProfile };